import { useState, useContext } from 'react'
import LogoImg from '../images/logo.png'

import {
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  Button,
  InputRightElement,
  useToast,
  Box,
  Text,
  Container,
  AbsoluteCenter,
  Divider,
  Icon,
  InputLeftElement,
} from '@chakra-ui/react'
import OAuth from '../components/OAuthGoogle/OAuth'

// firebase
import { getAuth, createUserWithEmailAndPassword, updateProfile } from 'firebase/auth'
import { doc, setDoc, serverTimestamp } from 'firebase/firestore'
import { db } from '../firebase.config'
import { Helmet } from 'react-helmet'

// react-router-dom
import { Link, useNavigate } from 'react-router-dom'

//user context
import UserContext from '../contexts/user/UserContext'
import { FaUser, FaEnvelope, FaLock, FaEye, FaEyeSlash, FaCar, FaUserPlus } from 'react-icons/fa'

const SignUp = () => {

  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: ''
  })
  const { fullName, email, password } = formData

  const { dispatch } = useContext(UserContext)

  const navigate = useNavigate()
  const toast = useToast()
  
  const changeHandler = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.id]: e.target.value
    }))
  }

  const submitHandler = async (e) => {
    e.preventDefault()
    const auth = getAuth()
    setLoading(true)
    try {
      const userCredentials = await createUserWithEmailAndPassword(
        auth, 
        email,
        password
      )
      const user = userCredentials.user
      
      await updateProfile(auth.currentUser, {
        displayName: fullName
      })

      const formDataCopy = {...formData}
      delete formDataCopy.password
      formDataCopy.timestamp = serverTimestamp()

      await setDoc(doc(db, 'users', user.uid), formDataCopy)
      dispatch({ type: 'SET_USER', payload: auth.currentUser.displayName})
      
      toast({
        title: 'Welcome aboard!',
        description: `Your account has been created successfully.`,
        status: 'success',
        duration: 4000,
        isClosable: true,
        position: 'top',
      })
      
      navigate('/')

    } catch (error) {
      let errorTitle = 'Registration Failed'
      let errorDescription = 'Something went wrong. Please try again later.'

      switch(error.code) {
        case 'auth/email-already-in-use':
          errorDescription = "This email is already registered. Please sign in instead."
          break
        case 'auth/weak-password':
          errorDescription = "Password should be at least 6 characters."
          break
        case 'auth/invalid-email':
          errorDescription = "Please enter a valid email address."
          break
        default:
          errorDescription = "Something went wrong. Please try again later."
      }
      
      toast({
        title: errorTitle,
        description: errorDescription,
        position: 'top',
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
      setLoading(false)     
    }
  }

  return (
    <>
      <Helmet>
        <title>Sign Up | NCPWheels</title>
        <meta
          name="description"
          content="Sign up to NCPWheels and find the best deals for your car"
        />
      </Helmet>

      <Box
        minH="100vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
        bgGradient="linear(to-br, gray.50, blue.50)"
        py={12}
        px={4}
        position="relative"
        overflow="hidden"
      >
        {/* Animated background elements */}
        <Box
          position="absolute"
          top="-20%"
          right="-10%"
          w="300px"
          h="300px"
          bgGradient="radial(circle, rgba(59,130,246,0.1) 0%, rgba(59,130,246,0) 70%)"
          borderRadius="full"
          zIndex={0}
        />
        <Box
          position="absolute"
          bottom="-20%"
          left="-10%"
          w="350px"
          h="350px"
          bgGradient="radial(circle, rgba(6,182,212,0.08) 0%, rgba(6,182,212,0) 70%)"
          borderRadius="full"
          zIndex={0}
        />

        <Container maxW="lg" position="relative" zIndex={1}>
          {/* Main Card */}
          <Box
            bg="white"
            borderRadius="3xl"
            boxShadow="2xl"
            overflow="hidden"
            transition="all 0.3s ease"
            _hover={{ boxShadow: "dark-lg" }}
          >
            {/* Decorative top bar */}
            <Box h="1" bgGradient="linear(to-r, blue.400, cyan.400, blue.500)" />

            <Box p={8}>
              {/* Logo and Title */}
              <Box textAlign="center" mb={8}>
                <Box
                  as="img"
                  src={LogoImg}
                  alt="NCPWheels"
                  mx="auto"
                  h="14"
                  mb={4}
                />
                <Text
                  fontSize="3xl"
                  fontWeight="extrabold"
                  bgGradient="linear(to-r, blue.600, cyan.600)"
                  bgClip="text"
                >
                  Create Account
                </Text>
                <Text color="gray.500" mt={2} fontSize="sm">
                  Join NCPWheels and start your journey
                </Text>
              </Box>

              {/* Sign Up Form */}
              <form onSubmit={submitHandler}>
                <FormControl mb={5} isRequired>
                  <FormLabel htmlFor="fullName" fontWeight="semibold" color="gray.700">
                    Full Name
                  </FormLabel>
                  <InputGroup size="lg">
                    <InputLeftElement pointerEvents="none">
                      <Icon as={FaUser} color="gray.400" boxSize={4} />
                    </InputLeftElement>
                    <Input
                      id="fullName"
                      type="text"
                      placeholder="John Doe"
                      value={fullName}
                      onChange={changeHandler}
                      required
                      borderRadius="xl"
                      borderColor="gray.200"
                      pl="10"
                      _hover={{ borderColor: "blue.300" }}
                      _focus={{
                        borderColor: "blue.400",
                        boxShadow: "0 0 0 1px #60A5FA",
                      }}
                    />
                  </InputGroup>
                </FormControl>

                <FormControl mb={5} isRequired>
                  <FormLabel htmlFor="email" fontWeight="semibold" color="gray.700">
                    Email Address
                  </FormLabel>
                  <InputGroup size="lg">
                    <InputLeftElement pointerEvents="none">
                      <Icon as={FaEnvelope} color="gray.400" boxSize={4} />
                    </InputLeftElement>
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={changeHandler}
                      required
                      borderRadius="xl"
                      borderColor="gray.200"
                      pl="10"
                      _hover={{ borderColor: "blue.300" }}
                      _focus={{
                        borderColor: "blue.400",
                        boxShadow: "0 0 0 1px #60A5FA",
                      }}
                    />
                  </InputGroup>
                </FormControl>

                <FormControl mb={4} isRequired>
                  <FormLabel htmlFor="password" fontWeight="semibold" color="gray.700">
                    Password
                  </FormLabel>
                  <InputGroup size="lg">
                    <InputLeftElement pointerEvents="none">
                      <Icon as={FaLock} color="gray.400" boxSize={4} />
                    </InputLeftElement>
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Create a password"
                      value={password}
                      onChange={changeHandler}
                      required
                      borderRadius="xl"
                      borderColor="gray.200"
                      pl="10"
                      pr="4.5rem"
                      _hover={{ borderColor: "blue.300" }}
                      _focus={{
                        borderColor: "blue.400",
                        boxShadow: "0 0 0 1px #60A5FA",
                      }}
                    />
                    <InputRightElement width="4.5rem" h="full">
                      <Button
                        h="2rem"
                        size="sm"
                        onClick={() => setShowPassword(!showPassword)}
                        variant="ghost"
                        borderRadius="full"
                      >
                        <Icon as={showPassword ? FaEyeSlash : FaEye} color="gray.500" boxSize={4} />
                      </Button>
                    </InputRightElement>
                  </InputGroup>
                </FormControl>

                {/* Links */}
                <Box display="flex" justifyContent="center" alignItems="center" mb={6}>
                  <Link to="/sign-in">
                    <Text fontSize="sm" color="blue.600" fontWeight="medium" _hover={{ color: "blue.700", textDecoration: "underline" }}>
                      Already have an account? Sign in
                    </Text>
                  </Link>
                </Box>

                {/* Sign Up Button */}
                <Button
                  type="submit"
                  isLoading={loading}
                  loadingText="Creating account..."
                  colorScheme="blue"
                  size="lg"
                  w="full"
                  borderRadius="xl"
                  bgGradient="linear(to-r, blue.500, blue.600)"
                  _hover={{
                    bgGradient: "linear(to-r, blue.600, blue.700)",
                    transform: "translateY(-2px)",
                    boxShadow: "lg",
                  }}
                  _active={{
                    transform: "translateY(0)",
                  }}
                  transition="all 0.2s"
                  mb={6}
                  leftIcon={<FaUserPlus />}
                >
                  Create Account
                </Button>

                {/* Divider */}
                <Box position="relative" mb={6}>
                  <Divider borderColor="gray.200" />
                  <AbsoluteCenter bg="white" px={4}>
                    <Text fontSize="sm" color="gray.500">
                      OR
                    </Text>
                  </AbsoluteCenter>
                </Box>

                {/* OAuth Section */}
                <OAuth />
              </form>
            </Box>
          </Box>

          {/* Footer note */}
          <Text textAlign="center" mt={6} fontSize="sm" color="gray.500">
            By signing up, you agree to our{' '}
            <Text as="span" color="blue.600" cursor="pointer" _hover={{ textDecoration: "underline" }}>
              Terms of Service
            </Text>{' '}
            and{' '}
            <Text as="span" color="blue.600" cursor="pointer" _hover={{ textDecoration: "underline" }}>
              Privacy Policy
            </Text>
          </Text>
        </Container>
      </Box>
    </>
  )
}

export default SignUp